/** Shared by the profile API and browser; never trust stored or submitted URLs. */
export const SOCIAL_PLATFORMS = [
  { id: 'instagram', label: '인스타그램', placeholder: '@아이디 또는 instagram.com/아이디' },
  { id: 'twitter', label: 'X(트위터)', placeholder: '@아이디 또는 x.com/아이디' },
  { id: 'threads', label: '스레드', placeholder: '@아이디 또는 threads.com/@아이디' },
  { id: 'facebook', label: '페이스북', placeholder: 'facebook.com/아이디 또는 프로필 주소' },
  { id: 'naver_blog', label: '네이버 블로그', placeholder: '블로그 아이디 또는 blog.naver.com/아이디' },
  { id: 'youtube', label: '유튜브', placeholder: '@핸들 또는 youtube.com/channel/채널ID' },
];

const hosts = {
  instagram: ['instagram.com', 'www.instagram.com'],
  twitter: ['x.com', 'www.x.com', 'twitter.com', 'www.twitter.com', 'mobile.twitter.com'],
  threads: ['threads.com', 'www.threads.com', 'threads.net', 'www.threads.net'],
  facebook: ['facebook.com', 'www.facebook.com', 'm.facebook.com'],
  naver_blog: ['blog.naver.com', 'm.blog.naver.com'],
  youtube: ['youtube.com', 'www.youtube.com', 'm.youtube.com'],
};
const origins = {
  instagram: 'https://www.instagram.com/', twitter: 'https://x.com/',
  threads: 'https://www.threads.com/@', facebook: 'https://www.facebook.com/',
  naver_blog: 'https://blog.naver.com/', youtube: 'https://www.youtube.com/@',
};
const reserved = {
  instagram: ['p', 'reel', 'reels', 'stories', 'explore', 'accounts', 'direct'],
  twitter: ['home', 'explore', 'search', 'intent', 'share', 'i', 'settings', 'messages', 'compose'],
  threads: ['login', 'search', 'activity'],
  facebook: ['login', 'sharer', 'sharer.php', 'share', 'watch', 'reel', 'groups', 'events', 'dialog', 'recover', 'settings'],
  naver_blog: ['postview.naver', 'postlist.naver'],
  youtube: ['watch', 'shorts', 'playlist', 'feed', 'results', 'redirect'],
};

export function normalizeSocialLink(platform, value) {
  const definition = SOCIAL_PLATFORMS.find(item => item.id === platform);
  if (!definition) throw new Error('지원하지 않는 SNS입니다.');
  const invalid = () => new Error(`${definition.label}: 올바른 계정 아이디 또는 프로필 주소를 입력해 주세요.`);
  if (value == null || value === '') return null;
  if (typeof value !== 'string') throw invalid();
  const input = value.trim();
  if (!input) return null;
  if (input.length > 500 || /[\s\\\u0000-\u001f\u007f]/u.test(input)) throw invalid();
  const handlePattern = platform === 'youtube' ? /^[\p{L}\p{N}_.-]+$/u : /^[a-zA-Z0-9_.-]+$/;
  const validateHandle = handle => {
    if (!handle || !handlePattern.test(handle) || reserved[platform].includes(handle.toLowerCase()) || /^\.+$/.test(handle)) throw invalid();
    return encodeURIComponent(handle);
  };
  const looksLikeUrl = /^[a-z][a-z\d+.-]*:/i.test(input) || input.includes('/');
  if (!looksLikeUrl) {
    // A bare service hostname is not an account handle.
    if (Object.values(hosts).flat().includes(input.toLowerCase())) throw invalid();
    return origins[platform] + validateHandle(input.replace(/^@/, ''));
  }
  let url;
  try { url = new URL(/^[a-z][a-z\d+.-]*:/i.test(input) ? input : `https://${input}`); }
  catch { throw invalid(); }
  if (!['http:', 'https:'].includes(url.protocol) || !hosts[platform].includes(url.hostname) || url.username || url.password || url.port) throw invalid();
  let path;
  try { path = decodeURIComponent(url.pathname).replace(/^\//, '').replace(/\/$/, ''); }
  catch { throw invalid(); }
  if (platform === 'facebook' && path === 'profile.php') {
    const id = url.searchParams.get('id');
    if (!id || !/^\d+$/.test(id)) throw invalid();
    return `https://www.facebook.com/profile.php?id=${id}`;
  }
  if (platform === 'youtube') {
    const channel = path.match(/^(channel|c|user)\/([^/]+)$/);
    if (channel) return `https://www.youtube.com/${channel[1]}/${validateHandle(channel[2])}`;
    if (!path.startsWith('@')) throw invalid();
  }
  if (platform === 'threads' && !path.startsWith('@')) throw invalid();
  return origins[platform] + validateHandle(path.replace(/^@/, ''));
}

/** PATCH replaces the supplied collection; empty input removes saved links. */
export function normalizeSocialLinks(value) {
  if (value == null) return {};
  if (typeof value !== 'object' || Array.isArray(value)) throw new Error('SNS 정보를 확인해 주세요.');
  const result = {};
  for (const [platform, input] of Object.entries(value)) {
    const url = normalizeSocialLink(platform, input);
    if (url) result[platform] = url;
  }
  return result;
}

/** Public rendering omits malformed legacy entries without hiding valid links. */
export function safeSocialLinks(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const result = {};
  for (const { id } of SOCIAL_PLATFORMS) {
    try {
      const url = normalizeSocialLink(id, value[id]);
      if (url) result[id] = url;
    } catch { /* Invalid public links are never rendered. */ }
  }
  return result;
}
